import { useOrgContext } from '@/org/contexts/OrgContext'
import useCircleParticipants from '@/participants/hooks/useCircleParticipants'
import { ThreadContext } from '@/thread/contexts/ThreadContext'
import { getDecidingCircleId } from '@rolebase/shared/helpers/getProposalScope'
import { ParticipantMember } from '@rolebase/shared/model/member'
import { ProposalVotersScope } from '@rolebase/shared/model/proposal'
import { useContext } from 'react'

// Resolve who can vote on a proposal.
//
// 'circle' scope (D6): the participants of the circle CONTAINING the object
// being changed, never the object's own holders — a role does not redefine
// itself. Goes through the same helper as the backend's loadProposal, so the
// tally shown here is the one the server actually applies.
//
// 'thread' scope: unchanged, the people on this tension. That one is an
// explicit choice by the author to consult the discussion itself.
//
// There may be no thread yet (a proposal created from a role's Security menu),
// so the thread context is optional and a circle id can be passed as fallback.
export default function useProposalVoters(
  votersScope: ProposalVotersScope,
  circleId?: string,
  decidingCircleId?: string | null
): ParticipantMember[] {
  const threadContext = useContext(ThreadContext)
  const { orgData } = useOrgContext()

  const baseCircleId = threadContext?.circle?.id ?? circleId
  // A settled deciding circle wins: it is the circle the author actually
  // changed. Without this, a link tension would be edited on one circle and
  // voted by the other, since the helper picks the grandparent on its own.
  // Only trusted among the tension's candidates, as on the server.
  const voterCircleId =
    orgData && baseCircleId
      ? getDecidingCircleId(orgData, baseCircleId, decidingCircleId)
      : (decidingCircleId ?? baseCircleId)

  const circleParticipants = useCircleParticipants(voterCircleId)
  return votersScope === 'circle'
    ? circleParticipants
    : (threadContext?.participants ?? [])
}
